import { useState } from 'react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { useAdjustCredits } from '@/hooks/useReferrals';

interface AdjustCreditsModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  currentCredits: number;
}

export default function AdjustCreditsModal({
  isOpen,
  onClose,
  userId,
  currentCredits,
}: AdjustCreditsModalProps) {
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');

  const adjustMutation = useAdjustCredits();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const numAmount = parseFloat(amount);
    if (isNaN(numAmount) || numAmount === 0) {
      setError('Informe um valor valido (positivo para adicionar, negativo para remover)');
      return;
    }

    if (!reason.trim()) {
      setError('Informe o motivo do ajuste');
      return;
    }

    try {
      await adjustMutation.mutateAsync({
        userId,
        input: { amount: numAmount, reason },
      });
      handleClose();
    } catch {
      setError('Erro ao ajustar creditos');
    }
  };

  const handleClose = () => {
    setAmount('');
    setReason('');
    setError('');
    onClose();
  };

  const newBalance = currentCredits + (parseFloat(amount) || 0);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Ajustar Creditos"
      size="md"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-500 dark:text-gray-400">Saldo atual:</span>
            <span className="font-bold text-gray-900 dark:text-white">
              R$ {currentCredits.toLocaleString()}
            </span>
          </div>
          {amount && !isNaN(parseFloat(amount)) && (
            <div className="flex justify-between items-center mt-2 pt-2 border-t border-gray-200 dark:border-gray-700">
              <span className="text-sm text-gray-500 dark:text-gray-400">Novo saldo:</span>
              <span className={`font-bold ${newBalance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                R$ {newBalance.toLocaleString()}
              </span>
            </div>
          )}
        </div>

        <Input
          label="Valor do ajuste (R$) *"
          type="number"
          step="0.01"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          placeholder="Ex: 10.00 (positivo) ou -5.00 (negativo)"
          hint="Use valores positivos para adicionar, negativos para remover"
        />

        <Textarea
          label="Motivo do ajuste *"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Ex: Bonus por campanha de marketing..."
          rows={3}
        />

        {error && (
          <p className="text-sm text-red-500 dark:text-red-400">{error}</p>
        )}

        <div className="flex justify-end gap-2 pt-4">
          <Button variant="ghost" type="button" onClick={handleClose}>
            Cancelar
          </Button>
          <Button
            type="submit"
            isLoading={adjustMutation.isPending}
          >
            Confirmar Ajuste
          </Button>
        </div>
      </form>
    </Modal>
  );
}
